import { useState, type FormEvent } from 'react'
import { SELECTABLE_WORKING_STATES, type WorkingState } from '@mads/shared'
import { Link, useNavigate } from '@tanstack/react-router'
import { ErrorNote, Loading } from '@/components/Feedback'
import { Select } from '@/components/Select'
import { MadsCodeField } from '@/components/MadsCodeField'
import { useCreateItem, useFixtureModels, useLocations } from '@/lib/queries'

/** Capture one lantern using its model's shared specification (issue #73). */
export function QuickAddLighting() {
  const navigate = useNavigate()
  const models = useFixtureModels()
  const locations = useLocations()
  const create = useCreateItem()
  const [modelId, setModelId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [madsCode, setMadsCode] = useState('')
  const [workingState, setWorkingState] = useState<WorkingState>('Not tested')
  const [notes, setNotes] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  if (models.error ?? locations.error) {
    return <ErrorNote error={models.error ?? locations.error} />
  }
  if (models.isPending || locations.isPending) {
    return <Loading what="Loading fixture models and locations" />
  }

  const model = models.data.find((entry) => String(entry.id) === modelId)

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (create.isPending) return
    if (model === undefined) {
      setValidationError('Choose a fixture model.')
      return
    }
    setValidationError(null)
    create.mutate({
      category: 'Lighting Fixture',
      name: model.name,
      brand: model.brand,
      fixtureModelId: model.id,
      quantity: 1,
      locationId: locationId === '' ? null : Number(locationId),
      madsCode: madsCode.trim() || null,
      workingState,
      notes: notes.trim() || null,
    }, {
      onSuccess: (item) =>
        void navigate({ to: '/inventory/$itemId', params: { itemId: String(item.id) } }),
    })
  }

  return (
    <>
      <h1 className="page-title">Quick add lighting fixture</h1>
      <p className="page-lede">Add one lantern. Its name, brand and optics come from the fixture model.</p>
      {models.data.length === 0 ? (
        <p className="empty-note">
          Add a <Link to="/fixtures">fixture model</Link> before adding a lighting fixture.
        </p>
      ) : (
        <form style={{ maxWidth: 520 }} onSubmit={handleSubmit}>
          <ErrorNote error={validationError === null ? null : new Error(validationError)} />
          <ErrorNote error={create.error} />
          <div className="field">
            <label htmlFor="quick-model">Fixture model</label>
            <Select inputId="quick-model" value={modelId} onChange={setModelId}
              placeholder="Choose a fixture model" isDisabled={create.isPending}
              options={models.data.map((entry) => ({ value: String(entry.id), label: [entry.brand, entry.name].filter(Boolean).join(' ') }))} />
          </div>
          <div className="field">
            <label htmlFor="quick-location">Location</label>
            <Select inputId="quick-location" value={locationId} onChange={setLocationId}
              isDisabled={create.isPending} options={[
                { value: '', label: 'No location recorded' },
                ...locations.data.map((entry) => ({ value: String(entry.id), label: entry.name })),
              ]} />
          </div>
          <MadsCodeField value={madsCode} onChange={setMadsCode} disabled={create.isPending} />
          <div className="field">
            <label htmlFor="quick-working-state">Working</label>
            <Select
              inputId="quick-working-state"
              value={workingState}
              isDisabled={create.isPending}
              onChange={(value) => setWorkingState(value as WorkingState)}
              options={SELECTABLE_WORKING_STATES.map((state) => ({ value: state, label: state }))}
            />
          </div>
          <div className="field">
            <label htmlFor="quick-notes">Notes</label>
            <textarea id="quick-notes" className="input" rows={3} value={notes}
              disabled={create.isPending} onChange={(event) => setNotes(event.target.value)} />
          </div>
          <div className="action-row" style={{ marginTop: 'var(--space-4)' }}>
            <button type="submit" className="btn btn-primary" disabled={create.isPending}>
              {create.isPending ? 'Saving…' : 'Add to inventory'}
            </button>
            <button type="button" className="btn btn-ghost" disabled={create.isPending}
              onClick={() => void navigate({ to: '/inventory' })}>Cancel</button>
          </div>
        </form>
      )}
    </>
  )
}
